import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WT from '@sscale/wtsdk';
import { SDKError, QueueStatus } from '@sscale/celebritysdk';

import { setStreamSettings } from 'store/reducers/CelebrityReducer';
import { setSessionError, setConnectedClb } from 'store/reducers/SessionReducer';
import Room from './Room/Room';
import './Rooms.css';
import { offVisibilityUpdate, onVisibilityUpdate } from 'utils/visibility';
import celebrityQueueAPI from 'services/celebrity';
import { ServiceChat } from 'services/ServiceChat';
import classnames from 'classnames';

function Rooms(props) {
  const { makeCelebrityLocal, isLoggedIn } = props;

  const dispatch = useDispatch();

  const { userName, constraints, tokens } = useSelector((state) => state.celebrity);
  const { sessionError } = useSelector((state) => state.session);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [pickedQueuer, setPickedQueuer] = useState(null);
  const [queuers, setQueuers] = useState([]);
  const [pageIsActive, setPageIsActive] = useState(true);

  useEffect(() => {
    if (!isLoggedIn) {
      return setPickedQueuer(null);
    }

    celebrityQueueAPI.currentQueuer().then((queuer) => {
      setPickedQueuer(queuer);
    });

    return () => {
      celebrityQueueAPI.unsubscribe();
    };
  }, [isLoggedIn, pageIsActive]);

  useEffect(() => {
    onVisibilityUpdate(setPageIsActive);
    return () => offVisibilityUpdate(setPageIsActive);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setQueuers([]);
    }

    if (!isLoggedIn || !pageIsActive) {
      return;
    }

    let timer;
    let aborted;
    const run = () => {
      celebrityQueueAPI
        .state()
        .then((state) => {
          if (aborted) {
            return;
          }

          setQueuers(state);
        })
        .catch((e) => {
          if (e.code === SDKError.CODE.NOT_AUTHORIZED) {
            throw e;
          } else {
            this.logger.warn(`Subscription error:`, e);
          }
        })
        .then(() => {
          if (aborted) {
            return;
          }

          timer = setTimeout(() => {
            run();
          }, 5000);
        })
        .catch((e) => console.log(e));
    };

    run();

    return () => {
      aborted = true;
      clearTimeout(timer);
    };
  }, [isLoggedIn, pageIsActive]);

  useEffect(() => {
    onVisibilityUpdate(setPageIsActive);
    return () => offVisibilityUpdate(setPageIsActive);
  }, []);

  const disconnect = () => {
    WT.Session.disconnect();
    dispatch(setConnectedClb(() => {}));
    // dispatch(setStreamSettings({ syncToken: '', stream: '' }));
    setIsConnected(false);
    makeCelebrityLocal();
  };

  const goNext = async () => {
    try {
      WT.Session.disconnect();
      setPickedQueuer(null);
      setIsConnecting(true);
      setIsConnected(false);

      const picked = await celebrityQueueAPI.goNext();
      await celebrityQueueAPI.state().then(setQueuers).catch(console.error);

      if (!picked) {
        throw new Error('Next queuer is not available');
      }

      setPickedQueuer(picked);

      connect(picked);
    } catch (e) {
      console.error(e);
      dispatch(setSessionError('Unable to pick next queuer. Please try again.'));
      setIsConnecting(false);
      setIsConnected(false);
    }
  };

  const connect = async (picked) => {
    if (!picked) {
      return;
    }

    try {
      setIsConnecting(true);

      dispatch(
        setConnectedClb(() => {
          setIsConnecting(false);
          setIsConnected(true);

          dispatch(setConnectedClb(() => {}));
        }),
      );

      await WT.Session.connect(picked.session_token, userName, constraints, { isCelebrity: true });

      // dispatch(setStreamSettings({ syncToken: picked.session_meta?.syncToken }));
    } catch (e) {
      console.error(e);
      dispatch(setSessionError('Unable to join the room. Please try again.'));
      setIsConnecting(false);
      setIsConnected(false);
      dispatch(setConnectedClb(() => {}));
    }
  };

  const publish = () => {
    WT.Session.sendMessage(`join_${tokens.wt}`);
    done(pickedQueuer.user_id);
  };

  const kick = (id, picked) => {
    celebrityQueueAPI
      .kick(id)
      .then(() => {
        if (picked) {
          setPickedQueuer(null);
        } else {
          setQueuers((queuers) => queuers.filter((q) => q.user_id !== id));
        }
      })
      .catch(console.error);
  };

  const done = (id) => {
    disconnect();
    celebrityQueueAPI
      .done(id)
      .then(() => {
        setPickedQueuer(null);
      })
      .catch(console.error);
  };

  const ready = () => {
    ServiceChat.publish(ServiceChat.CUSTOM_EVENTS.USER_READY);
  };

  useEffect(() => {
    if (sessionError) {
      WT.Session.disconnect();
      // dispatch(setStreamSettings({ syncToken: '', stream: '' }));
      setIsConnecting(false);
      setIsConnected(false);
    }
  }, [sessionError]);

  const hasNext = queuers.find((queuer) => queuer.status === QueueStatus.CONNECTED);

  return (
    <div className="rooms">
      <div className="rooms-next">
        <button
          className={classnames('room-next-button', 'button')}
          onClick={goNext}
          disabled={isConnecting || isConnected || !hasNext}
        >
          {isConnecting ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          ) : (
            <>Go next</>
          )}
        </button>
      </div>
      <div className="rooms-connected">
        {pickedQueuer && (
          <Room
            duration={Date.now() - new Date(pickedQueuer.created_at).getTime()}
            id={pickedQueuer.user_id}
            connected={isConnected}
            picked
            disabled={isConnecting}
            loading={isConnecting}
            onConnect={() => connect(pickedQueuer)}
            onDisconnect={() => disconnect()}
            onPublish={() => publish()}
            onFinish={() => done(pickedQueuer.user_id)}
            onKick={() => kick(pickedQueuer.user_id, true)}
            onReady={() => ready()}
          />
        )}
      </div>

      <div className="rooms-content">
        <div className="room-status-block">WEBCAM READY</div>
        {queuers
          .filter((queuer) => queuer.status === QueueStatus.CONNECTED)
          .map((queuer) => (
            <Room
              key={queuer.user_id}
              duration={Date.now() - new Date(queuer.created_at).getTime()}
              id={queuer.user_id}
              onKick={() => kick(queuer.user_id)}
            />
          ))}

        <div className="room-status-block">WAITING FOR WEBCAM</div>
        {queuers
          .filter((queuer) => queuer.status === QueueStatus.CONNECTION_REQUIRED)
          .map((queuer) => (
            <Room
              key={queuer.user_id}
              duration={Date.now() - new Date(queuer.created_at).getTime()}
              id={queuer.user_id}
              countdown={queuer.action_required_countdown}
              onKick={() => kick(queuer.user_id)}
            />
          ))}

        <div className="room-status-block">WAITING</div>
        {queuers
          .filter((queuer) => queuer.status === QueueStatus.PENDING)
          .map((queuer) => (
            <Room
              key={queuer.user_id}
              duration={Date.now() - new Date(queuer.created_at).getTime()}
              id={queuer.user_id}
              onKick={() => kick(queuer.user_id)}
            />
          ))}
      </div>
    </div>
  );
}

export default Rooms;
